import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Button, Loader, Slider, Text } from "@mantine/core";
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import styled from "styled-components";
import { theme } from "../../theme";
import {
  GeocodeHit,
  parseCoordinates,
  reverseGeocode,
  searchAddress,
} from "../../utils/geocoding";

// Fix leaflet's default icon paths broken by webpack
const iconDefault = L.icon({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const DEFAULT_CENTER: [number, number] = [39.9334, 32.8597]; // Ankara

interface Props {
  latitude: number | null;
  longitude: number | null;
  geofenceRadius: number;
  onChange: (lat: number, lng: number, radius: number) => void;
}

/**
 * Şirket konumu / geofence seçici.
 *
 * Eskiden tek yol vardı: Ankara'da zoom 6 açılan haritada doğru sokağı bulana
 * kadar yakınlaştırıp tıklamak. Kullanıcı için en zor kısım burasıydı, üç yol
 * eklendi:
 *   1. Adres arama (Nominatim) — "Acıbadem Mah. Kadıköy" yazıp Enter
 *   2. Google Maps'ten kopyalanan koordinat/bağlantıyı aynı kutuya yapıştırma
 *   3. "Konumumu Kullan" — İK zaten çoğunlukla iş yerinde oturuyor
 * Pin ayrıca sürüklenebilir ve seçilen noktanın adresi altta yazılı görünüyor;
 * koordinata bakarak doğru yeri seçtiğini anlamak mümkün değildi.
 */
const CompanyLocationPicker = ({
  latitude,
  longitude,
  geofenceRadius,
  onChange,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  // Tıklama/sürükleme kapanışları hep güncel değeri görsün
  const latRef = useRef(latitude);
  const lngRef = useRef(longitude);
  const radiusRef = useRef(geofenceRadius);
  const onChangeRef = useRef(onChange);

  useEffect(() => { latRef.current = latitude; });
  useEffect(() => { lngRef.current = longitude; });
  useEffect(() => { radiusRef.current = geofenceRadius; });
  useEffect(() => { onChangeRef.current = onChange; });

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GeocodeHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);

  const paint = useCallback(
    (map: L.Map, lat: number, lng: number, radius: number) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng], {
          icon: iconDefault,
          draggable: true,
          autoPan: true,
        }).addTo(map);
        // Kaba tıklamadan sonra ince ayar: pin sürüklenebilir.
        markerRef.current.on("dragend", () => {
          const { lat: dLat, lng: dLng } = markerRef.current!.getLatLng();
          circleRef.current?.setLatLng([dLat, dLng]);
          onChangeRef.current(dLat, dLng, radiusRef.current);
        });
      }

      if (circleRef.current) {
        circleRef.current.setLatLng([lat, lng]).setRadius(radius);
      } else {
        circleRef.current = L.circle([lat, lng], {
          radius,
          color: theme.colors.brand.normal,
          fillColor: theme.colors.brand.normal,
          fillOpacity: 0.15,
          weight: 2,
        }).addTo(map);
      }
    },
    []
  );

  /** Haritayı verilen noktaya taşır ve formu günceller. */
  const moveTo = useCallback(
    (lat: number, lng: number, zoom = 16) => {
      const map = mapRef.current;
      if (!map) return;
      paint(map, lat, lng, radiusRef.current);
      map.setView([lat, lng], zoom);
      onChangeRef.current(lat, lng, radiusRef.current);
    },
    [paint]
  );

  // Harita bir kez kurulur
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current).setView(DEFAULT_CENTER, 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      paint(map, lat, lng, radiusRef.current);
      onChangeRef.current(lat, lng, radiusRef.current);
    });

    mapRef.current = map;

    if (latRef.current && lngRef.current) {
      paint(map, latRef.current, lngRef.current, radiusRef.current);
      map.setView([latRef.current, lngRef.current], 15);
    }

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
  }, [paint]);

  // Yarıçap / dışarıdan gelen koordinat değişimi
  useEffect(() => {
    if (!mapRef.current || !latitude || !longitude) return;
    paint(mapRef.current, latitude, longitude, geofenceRadius);
  }, [geofenceRadius, latitude, longitude, paint]);

  // Seçilen noktanın adresi. Gecikmeli: sürükleme/tıklama sırasında her ara
  // konum için istek atılmasın (Nominatim saniyede 1 istek istiyor).
  useEffect(() => {
    if (!latitude || !longitude) {
      setAddress(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setAddress(await reverseGeocode(latitude, longitude, controller.signal));
      } catch {
        setAddress(null); // adres çözülemezse harita yine çalışıyor
      }
    }, 800);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [latitude, longitude]);

  const runSearch = async () => {
    const text = query.trim();
    if (!text) return;

    setMessage(null);
    setResults(null);

    // Önce koordinat mı diye bakılır: yapıştırılan Google Maps bağlantısı için
    // ağa çıkmaya gerek yok.
    const parsed = parseCoordinates(text);
    if (parsed) {
      moveTo(parsed.latitude, parsed.longitude, 17);
      setQuery("");
      return;
    }

    setSearching(true);
    try {
      const hits = await searchAddress(text);
      if (hits.length === 0) {
        setMessage("Adres bulunamadı. Farklı bir ifade deneyin.");
      } else if (hits.length === 1) {
        moveTo(hits[0].latitude, hits[0].longitude);
        setQuery("");
      } else {
        setResults(hits);
      }
    } catch {
      setMessage("Adres servisine ulaşılamadı. Haritadan seçebilirsiniz.");
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setMessage("Tarayıcınız konum servisini desteklemiyor.");
      return;
    }
    setMessage(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        moveTo(position.coords.latitude, position.coords.longitude, 17);
      },
      (error) => {
        setLocating(false);
        setMessage(
          error.code === error.PERMISSION_DENIED
            ? "Konum izni verilmedi. Tarayıcı ayarlarından izin verebilirsiniz."
            : "Konum alınamadı. Haritadan seçebilirsiniz."
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <Wrapper>
      <Text fw={600} fz={14} mb={8}>
        Şirket Konumu
      </Text>
      <Text fz={12} c="dimmed" mb={12}>
        Adres arayın, Google Maps'ten kopyaladığınız koordinatı yapıştırın veya
        haritaya tıklayın. Bu konum, çalışanların yaklaştığında BLE kilit açma
        servisini otomatik başlatmak için kullanılır.
      </Text>

      <SearchRow>
        <SearchBox>
          <MagnifyingGlassIcon width={20} />
          <SearchInput
            value={query}
            placeholder="Adres ara veya koordinat yapıştır (40.995131, 29.038142)"
            aria-label="Adres ara veya koordinat yapıştır"
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // form gönderimini tetiklemesin
                runSearch();
              }
            }}
          />
          {searching && <Loader size={16} color={theme.colors.brand.normal} />}
        </SearchBox>
        <Button
          variant="default"
          radius={10}
          h={44}
          leftSection={<MapPinIcon width={18} />}
          loading={locating}
          onClick={useMyLocation}
        >
          Konumumu Kullan
        </Button>
        {/* İkon yok: büyüteç zaten kutunun solunda; buton yalnızca Enter'a
            basmayan kullanıcı için ikinci bir yol. */}
        <Button radius={10} h={44} px={24} color={theme.colors.brand.normal} onClick={runSearch}>
          Ara
        </Button>
      </SearchRow>

      {message && <Hint $error>{message}</Hint>}

      {results && (
        <ResultList>
          {results.map((hit) => (
            <ResultItem
              key={`${hit.latitude},${hit.longitude}`}
              type="button"
              onClick={() => {
                moveTo(hit.latitude, hit.longitude);
                setResults(null);
                setQuery("");
              }}
            >
              <MapPinIcon width={16} />
              <span>{hit.label}</span>
            </ResultItem>
          ))}
        </ResultList>
      )}

      <MapWrapper ref={containerRef} />

      <RadiusRow>
        <Text fz={13} fw={500}>
          Geofence Yarıçapı: <strong>{geofenceRadius} metre</strong>
        </Text>
        <Slider
          min={100}
          max={1000}
          step={50}
          value={geofenceRadius}
          onChange={(value) => {
            if (latitude && longitude) onChange(latitude, longitude, value);
          }}
          marks={[
            { value: 100, label: "100m" },
            { value: 500, label: "500m" },
            { value: 1000, label: "1km" },
          ]}
          color={theme.colors.brand.normal}
          styles={{ markLabel: { fontSize: 11 } }}
        />
      </RadiusRow>

      {latitude && longitude && (
        <Selected>
          <SelectedAddress>{address ?? "Adres çözümleniyor…"}</SelectedAddress>
          <Text fz={11} c="dimmed">
            Koordinat: {latitude.toFixed(6)}, {longitude.toFixed(6)} · Pini
            sürükleyerek ince ayar yapabilirsiniz.
          </Text>
        </Selected>
      )}
    </Wrapper>
  );
};

export default CompanyLocationPicker;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 260px;
  height: 44px;
  padding: 0 14px;
  border: 1px solid ${theme.colors.greyscale.gray40};
  border-radius: 10px;
  background: #ffffff;

  svg {
    color: ${theme.colors.brand.normal};
    flex-shrink: 0;
  }

  &:focus-within {
    border-color: ${theme.colors.brand.normal};
    box-shadow: 0px 0px 5px 3px #5c26ff14;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 15px;
  color: ${theme.colors.greyscale.black};
  background: transparent;

  &::placeholder {
    color: ${theme.colors.greyscale.gray60};
  }
`;

const Hint = styled.div<{ $error?: boolean }>`
  font-size: 12px;
  margin-bottom: 10px;
  color: ${({ $error }) => ($error ? theme.colors.alert.red : "#64748b")};
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  text-align: left;
  font-size: 13px;
  color: #334155;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f8fafc;
  }
  svg {
    color: ${theme.colors.brand.normal};
    flex-shrink: 0;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 320px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  cursor: crosshair;
`;

const RadiusRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;

  /*
   * Kaydırıcının uç işaret etiketleri (100m ve 1km) işaretin ORTASINA
   * hizalanıyor, yani yarısı kutunun dışında kalıyor. Geniş ekranda kimse fark
   * etmiyordu; 390px'te bu 15px doğrudan sayfa kenarına dayanıyordu.
   * Kaydırıcıya iki yandan pay bırakmak etiketleri içeri alıyor.
   */
  .mantine-Slider-root {
    padding: 0 14px;
  }
`;

// Kaydırıcının 100m/500m/1km etiketleriyle çakışmasın diye biraz boşluk.
const Selected = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 22px;
`;

const SelectedAddress = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.greyscale.black};
`;
